/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, Suspense, lazy, startTransition, useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import type { ShopperProducts } from '@/scapi';
import { Button } from '@/components/ui/button';
import { useProductView } from '@/providers/product-view';
import { useCurrentVariant } from '@/hooks/product/use-current-variant';
import { isProductSet, isProductBundle } from '@/lib/product/product-utils';
import { useCheckAndExecutePendingAction } from '@/hooks/check-and-execute-pending-action';
import { useTranslation } from 'react-i18next';
import { UITarget } from '@/targets/ui-target';
import AnimatedButton from '@/components/animated-button';
import { ArrowRightIcon } from '@/components/icons';
import { useNavigate } from '@/hooks/use-navigate';
import { routes } from '@/route-paths';

/** @feature-stub Express checkout buttons — remove this import and its JSX below to strip the stub */
const ExpressPayments = lazy(() => import('@/components/checkout/components/express-payments'));

interface ProductCartActionsProps {
    product: ShopperProducts.schemas['Product'];
    /** Called immediately before cart action starts (add or update) - useful for optimistic UI like closing modal */
    onBeforeCartAction?: () => void;
    /** Called after successful cart operation completes (add or update) */
    onCartSuccess?: () => void;
    /** Called if cart operation fails (add or update) */
    onCartError?: (error: unknown) => void;
    /** Called immediately before add to wishlist action starts */
    onBeforeAddToWishlist?: () => void;
    /** Called after successful add to wishlist action completes */
    onAddToWishlistSuccess?: () => void;
    /** Called if add to wishlist operation fails */
    onAddToWishlistError?: (error: unknown) => void;
    /**
     * When provided in add mode, renders a compact two-button layout:
     * "Add to Cart" + "Buy it Now" side by side. Express payments, BNPL,
     * wishlist, and share buttons are hidden in this layout.
     * Typically navigates to the PDP for the full purchase flow.
     */
    onBuyNow?: () => void;
    /** Is being rendered inside the quick add modal */
    isQuickAdd?: boolean;
    /** Overrides the "Add to Cart" label in add mode (e.g. "Move to Cart" from the wishlist) */
    addToCartLabel?: string;
}

export default function ProductCartActions({
    product,
    onBeforeCartAction,
    onCartSuccess,
    onCartError,
    onBeforeAddToWishlist,
    onAddToWishlistSuccess,
    onAddToWishlistError,
    onBuyNow,
    isQuickAdd = false,
    addToCartLabel,
}: ProductCartActionsProps): ReactElement {
    const { t } = useTranslation('product');
    const resolvedAddToCartLabel = addToCartLabel ?? t('addToCart');
    const isProductASet = isProductSet(product);
    const isProductABundle = isProductBundle(product);

    const currentVariant = useCurrentVariant({ product });

    // Get shared state from context
    const {
        mode,
        isAddingToOrUpdatingCart,
        canAddToCart,
        isMasterOrVariantProduct,
        handleAddToCart,
        handleUpdateCart,
        handleAddToWishlist,
        cartAddData,
    } = useProductView();

    const isEditMode = mode === 'edit';
    // Compact layout: the Quick Add modal (add mode with a "Buy it Now" handler), and the cart edit modal so it
    // looks the same as Quick Add. Hides express payments, BNPL, wishlist, and share — shopper goes to PDP for those.
    const isCompactAddMode = (!isEditMode && !!onBuyNow) || (isEditMode && !!isQuickAdd);

    // Get product ID for pending action matching
    const productToCheck = isMasterOrVariantProduct ? currentVariant : product;
    const currentProductId = productToCheck?.productId || product.id;

    // Check for pending actions and execute if they match this product
    // This handles actions that were initiated before authentication (e.g., addToWishlist)
    useCheckAndExecutePendingAction({
        actionName: 'addToWishlist',
        shouldExecute: (params) => params.productId === currentProductId,
        onMatch: async () => {
            const productToAdd = isMasterOrVariantProduct ? currentVariant : product;
            // Call before callback
            onBeforeAddToWishlist?.();
            try {
                await handleAddToWishlist(productToAdd as ShopperProducts.schemas['Variant']);
                // Call success callback after API completes
                onAddToWishlistSuccess?.();
            } catch (error) {
                onAddToWishlistError?.(error);
                throw error;
            }
        },
    });

    const navigate = useNavigate();

    const onAddOrUpdateToCart = async () => {
        // Keep edit-mode optimistic close behavior, but for add-mode quick-add we
        // wait for success so the mounted hook can emit toast + open mini-cart.
        if (isEditMode) {
            onBeforeCartAction?.();
        }

        try {
            // Use handleUpdateCart in edit mode, handleAddToCart in add mode
            if (isEditMode) {
                await handleUpdateCart();
            } else {
                await handleAddToCart();
            }
            // Call success callback after API completes
            onCartSuccess?.();
        } catch (error) {
            onCartError?.(error);
        }
    };

    const pendingBuyNowRef = useRef(false);

    const handleBuyNow = () => {
        if (!canAddToCart || isAddingToOrUpdatingCart) return;
        pendingBuyNowRef.current = true;
        void handleAddToCart();
    };

    const prevIsAddingRef = useRef(false);
    useEffect(() => {
        const wasAdding = prevIsAddingRef.current;
        prevIsAddingRef.current = isAddingToOrUpdatingCart;
        if (!wasAdding || isAddingToOrUpdatingCart) return;
        if (!pendingBuyNowRef.current) return;

        pendingBuyNowRef.current = false;

        if (cartAddData?.success === false) return;

        void navigate(routes.checkout);
    }, [isAddingToOrUpdatingCart, cartAddData, navigate]);

    // Defer ExpressPayments loading until after initial render to improve Lighthouse performance
    const [shouldLoadExpressPayments, setShouldLoadExpressPayments] = useState(false);

    useEffect(() => {
        // Use startTransition to mark this as non-urgent, allowing initial render to complete first
        startTransition(() => {
            setShouldLoadExpressPayments(true);
        });
    }, []);

    return (
        <div className={cn("pdp-cart-actions-outer", isQuickAdd ? "mt-4" : "mt-6")}>
            {/* Options Selection Message */}
            {!isQuickAdd && isMasterOrVariantProduct && !currentVariant && !isProductASet && !isProductABundle && (
                <div className="text-destructive font-medium">{t('selectAllOptions')}</div>
            )}
            <UITarget targetId="sfcc.pdp.tax.productMessage" />

            {/* Action Buttons */}
            <div className="flex flex-col gap-3">
                {/* Compact layout (Quick Add modal): Add to Cart only */}
                {isCompactAddMode && !isProductASet && !isProductABundle && (
                    <div className="w-full">
                        <AnimatedButton
                            component="button"
                            onClick={() => void onAddOrUpdateToCart()}
                            disabled={!canAddToCart || isAddingToOrUpdatingCart}
                            variant="secondary"
                            icon={<ArrowRightIcon className="size-8" strokeWidth={1.5} />}
                            className="text-[16px] font-medium h-12 md:h-13 w-full bg-[#111113] hover:bg-black rounded-full border-none shadow-none text-white">
                            {/* Same label in the cart edit modal; there it replaces the existing line rather than adding a second one */}
                            {isAddingToOrUpdatingCart ? t('addingToCart') : resolvedAddToCartLabel}
                        </AnimatedButton>
                    </div>
                )}

                {/* Standard layout: single Add to Cart / Update button */}
                {!isCompactAddMode && !isProductASet && !isProductABundle && (
                    <div className="pdp-sticky-buttons flex gap-2 w-full">
                        <AnimatedButton
                            component="button"
                            variant="primary"
                            data-testid="buy-now"
                            disabled={!canAddToCart || isAddingToOrUpdatingCart}
                            onClick={() => void handleBuyNow()}
                            className="w-full text-base font-semibold leading-6 py-4 h-[62px]"
                            size="lg"
                        >
                            {isAddingToOrUpdatingCart ? 'Please wait...' : 'Buy Now'}
                        </AnimatedButton>
                        <AnimatedButton
                            component="button"
                            variant="secondary"
                            data-testid="add-to-cart"
                            onClick={() => void onAddOrUpdateToCart()}
                            disabled={!canAddToCart || isAddingToOrUpdatingCart}
                            className="w-full h[20px] text-base font-semibold leading-6 py-4 h-[62px]"
                            size="lg">
                            {isEditMode
                                ? t('updateCart')
                                : isAddingToOrUpdatingCart
                                  ? t('addingToCart')
                                  : resolvedAddToCartLabel}
                        </AnimatedButton>
                    </div>
                )}

                {/* Delivery Options / Pincode Checker */}
                {!isCompactAddMode && !isProductASet && !isProductABundle && (
                    <PincodeChecker />
                )}

                {/* Express Payments — standard layout only, vertical for PDP */}
                {/* {!isCompactAddMode &&
                    !isProductASet &&
                    !isProductABundle &&
                    !isEditMode &&
                    shouldLoadExpressPayments && (
                        <UITarget targetId="sfcc.pdp.payments.expressCheckout">
                            <Suspense fallback={null}>
                                <ExpressPayments
                                    layout="vertical"
                                    separatorPosition="top"
                                    separatorText={t('expressPayments.separatorBuyWith')}
                                    disabled={!canAddToCart}
                                />
                            </Suspense>
                        </UITarget>
                    )} */}

                <UITarget targetId="sfcc.pdp.after.addToCart" />
                {/* {!isCompactAddMode && !isEditMode && currentProductId && <UITarget targetId="sfcc.pdp.bnpl.message" />} */}
            </div>
        </div>
    );
}

function PincodeChecker() {
    const [pincode, setPincode] = useState('');
    const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [message, setMessage] = useState('Please enter PIN code to check delivery info');

    const handleCheck = () => {
        if (!pincode) {
            setStatus('error');
            setMessage('Please enter a PIN code');
            return;
        }
        if (!/^\d{6}$/.test(pincode)) {
            setStatus('error');
            setMessage('Please enter a valid 6-digit PIN code');
            return;
        }

        setStatus('success');
        setMessage('Delivery is available for this pincode.');
    };

    return (
        <div className="mt-3">
            <h4 className="mb-2 text-base font-semibold text-foreground pdp-delivery-heading">Delivery Options</h4>
            <div className="relative w-full  md:w-[50%]">
                <input
                    type="text"
                    placeholder="Enter pincode"
                    value={pincode}
                    onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setPincode(val);
                        if (status !== 'idle') {
                            setStatus('idle');
                            setMessage('Please enter PIN code to check delivery info');
                        }
                    }}
                    className="w-full h-11 px-3 pr-16 border border-[#B3B3B3] rounded-[4px] focus:outline-none focus:border-black text-sm bg-white"
                />
                <button
                    type="button"
                    onClick={handleCheck}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold leading-[18px] tracking-[0.07em] text-black hover:text-black/70 focus:outline-none"
                >
                    Check
                </button>
            </div>
             <p
        className={`mt-1 text-[10px]! pdp-delivery-message leading-[22px] ${
            status === 'success'
                ? 'font-medium text-[#098338]'
                : status === 'error'
                  ? 'font-medium text-destructive'
                  : 'text-[#35353B]'
        }`}>
        {message}
    </p>
        </div>
    );
}
